<script>
	import Vue from "vue";
	import { mapGetters, mapState } from "vuex";
	import Draggable from "vuedraggable";

	/**
	 * Billing
	 */
	import BillingDropdownHead from "./DropdownHead";
	import BillingEntry from "./Entry";
	import BillingSection from "./Section";
	import BillingComment from "./Comment";
	import BillingSubtotal from "./Subtotal";

	export default {
		components: {
			Draggable,
			BillingDropdownHead,
			BillingEntry,
			BillingSection,
			BillingComment,
			BillingSubtotal
		},
		data() {
			return {
				allowEdit: true,
				productList: []
			};
		},
		computed: {
			...mapGetters(["enabledHeaders", "searchAllRows", "vatsValues", "numbered_sections", "mostUsedVatRate"]),
			...mapState({
				appId: state => state.app.id,
				table: state => state.billing.table,
				billing: state => state.billing,
				selectedVatValue: state => state.billing.selectedVatValue,
				background: state => state.settings.accentColor.background,
				foreground: state => state.settings.accentColor.foreground,
				loadedDocument: state => state.home.loadedDocument,
				isEditable: state => state.home.isEditable,
				is_superadmin: state => state.home.is_superadmin,
				isRecurringInvoice: state => state.recurring.isRecurringInvoice
			})
		},
		watch: {
			table: {
				handler(newVal) {
					/*this.$nextTick(function () {
						this.checkVatValues();
					});*/

					// if the table is completely empty add a basic section
					if (newVal.length === 0) {
						this.inititalizeTable();
					}
				},
				deep: true
			}
		},
		async created() {
			if (!this.loadedDocument || (this.loadedDocument && this.loadedDocument?.content?.length === 0)) {
				this.inititalizeTable();
			}
		},
		methods: {
			inititalizeTable() {
				this.$store.commit("enableDefaultHeaders");

				Vue.set(this.table, null, {
					sections: [{}],
					totals: {
						exclTax: 0,
						vat: 0,
						inclTax: 0
					}
				});

				this.appendRow({ component: "entry", sectionId: 0 });
				this.appendRow({ component: "subtotal", sectionId: 0 });
			},
			tabPress(sectionId, rowId, event) {
				const searchPath = (path, tagName) => path.find(el => el.localName === tagName);

				const parentTd = searchPath(event.composedPath(), "td");
				const previousElementSibling = parentTd.previousElementSibling;

				// Ignore the enter key on the designation input
				if (event.keyCode === 13 && !previousElementSibling) {
					return null;
				}

				const nextElementSibling = parentTd.nextElementSibling;
				const isLastRow = rowId === this.table.sections[sectionId].length - 2; // Minus two, 1 for the .length, 1 for the subtotal

				// Only apply when it's the last row
				event.preventDefault();

				// Case 1 : There is no next element and it's the last row
				if (!nextElementSibling && isLastRow) {
					this.appendRow({ component: "entry", sectionId, data: {vatPercentage: (this.selectedVatValue?? this.mostUsedVatRate)}});
				} else {
					const secondNextElementSibling = nextElementSibling?.nextElementSibling;

					// Case 2 : The next input is not readonly and is not the last one in the row
					if (!nextElementSibling?.firstChild?.getAttribute("readonly") && secondNextElementSibling) {
						nextElementSibling.firstChild.focus();
					} else {
						// Case 3 : The next input is readonly but not the last one in the row, ignore it and move to the second sibling
						if (secondNextElementSibling) {
							secondNextElementSibling.firstChild.focus();
						}
						// Case 4 : There is no next sibling and it's not the last row, just go to the next row
						else if (!secondNextElementSibling && !isLastRow) {
							const parentTr = searchPath(event.composedPath(), "tr");
							const nextElementSiblingTr = parentTr.nextElementSibling;

							nextElementSiblingTr.firstElementChild.focus();
						}
						// Case 5 : The next input is readonly and is the last one in the row
						else if (isLastRow) {
							this.appendRow({ component: "entry", sectionId, data: {vatPercentage: (this.selectedVatValue?? this.mostUsedVatRate)}});
						}
					}
				}
			},
			isSectionExistant(sectionId) {
				return !!this.table.sections[sectionId].find(row => row.component === "section");
			},
			moveSectionToTop(sectionId) {
				const position = this.table.sections[sectionId].findIndex(row => row.component === "section");

				if (position !== -1) {
					const component = this.table.sections[sectionId].splice(position, 1)[0];

					this.table.sections[sectionId].splice(0, 0, component);
				}
			},
			moveSubtotalToBottom(sectionId) {
				const position = this.table.sections[sectionId].findIndex(row => row.component === "subtotal");

				if (position !== -1 && position !== this.table.sections[sectionId].length - 1) {
					const component = this.table.sections[sectionId].splice(position, 1)[0];

					this.table.sections[sectionId].splice(this.table.sections[sectionId].length, 0, component);
				}
			},
			createRow({ component, sectionId, data, position }) {
				const row = {
					key: component + Math.floor(Math.random() * 100000 + 1),
					component,
					data
				};

				if (typeof position !== "undefined") {
					this.table.sections[sectionId].splice(position, 0, row);
					Vue.set(this.table.sections, sectionId, this.table.sections[sectionId]);
				} else {
					Vue.set(this.table.sections[sectionId], this.table.sections[sectionId].length, row);
				}
			},
			appendRow({ component, sectionId, data, position }) {
				if (this.table.sections.length === 0) {
					Vue.set(this.table.sections, 0, []);
				}

				if (component === "section") {
					// If it's the first created section, place it at the top of the table
					if (this.isSectionExistant(sectionId) === false) {
						this.createRow({
							component: "section",
							sectionId,
							data,
							position: 0
						});
					} else {
						sectionId += 1;

						this.table.sections.splice(sectionId, 0, []);

						// If we are creating a section, we bundle it with an entry and a subtotal
						this.createRow({
							component: "section",
							sectionId: sectionId
						});
						this.createRow({
							component: "entry",
							sectionId: sectionId
						});
						this.createRow({
							component: "subtotal",
							sectionId: sectionId
						});
					}
				} else {
					position = typeof position !== "undefined" ? position : this.table.sections[sectionId].length;

					this.createRow({
						component,
						sectionId,
						data,
						position
					});

					this.moveSubtotalToBottom(sectionId);
				}
			},
			removeRow({ sectionId, index }) {
				const row = this.table.sections[sectionId][index];

				const removeLine = () => {
					if (typeof index !== "undefined") {
						this.table.sections[sectionId].splice(index, 1);
					} else {
						this.table.sections.splice(sectionId, 1);
					}

					// Delete the section if the only component left is the subtotal
					if (
						this.table.sections &&
						this.table.sections[sectionId] &&
						this.table.sections[sectionId].length === 1
					) {
						this.table.sections.splice(sectionId, 1);
					}
				};

				if (row && row.component === "entry" && row.data.designation.length > 0) {
					this.$bvModal
						.msgBoxConfirm("Voulez-vous vraiment supprimer cette ligne ?", {
							title: "Confirmer la suppression",
							size: "sm",
							buttonSize: "sm",
							okVariant: "danger",
							okTitle: "Oui",
							cancelTitle: "Non",
							footerClass: "p-2",
							hideHeaderClose: true,
							centered: true
						})
						.then((value) => {
              if (value)
							  removeLine();
						})
						.catch(error => {
							console.error(error);
						});
				} else {
					removeLine();
				}
			},
			clear(sectionId, rowIndex) {
				const section = this.table.sections[sectionId];
				const line = this.table.sections[sectionId][rowIndex];

				// filter out the section header and the subtotal line
				const filteredSection = section.filter(item => !["section", "subtotal"].includes(item.component));

				// check if their is other lines in the section
				// if the section is more than 2 items long
				if (filteredSection.length > 1) {
					// check if the line is empty

					// if data undefnied => delete
					if (typeof line.data === "undefined") {
						this.removeRow({ sectionId, index: rowIndex });
					}
					// if the total and the designation are at 0
					// and allowDelete is 1 all fields are at default value, next delete stroke should remove the line => pass allowDelete to 2
					// if allowDelete is 2 delete the line
					else if (line.data.designation === "" && line.data.totalInclTax == 0) {
						if (line.data.allowDelete == 1) {
							line.data.allowDelete = 2;
						} else if (line.data.allowDelete == 2) {
							this.removeRow({ sectionId, index: rowIndex });
						}
					}
				}

				return line;
			},
			billingTableEdition(status) {
				this.allowEdit = status;
			}
		}
	};
</script>

<template>
	<!-- <div style="border-radius: 6px 6px 0 6px; overflow-x: hidden;"> -->
	<div>
		<div v-if="!is_superadmin && !isRecurringInvoice && (!allowEdit || !isEditable)" class="disable-table">
			Conformément à la loi
			<a target="_blank" href="https://www.legifrance.gouv.fr/loda/article_lc/LEGIARTI000031734740/">
				Anti-Fraude TVA
			</a>
			, <br>
			il n'est pas possible de modifier le contenu d'une facture confirmée.
		</div>

		<b-table-simple ref="table" small fixed class="billing__table mb-0">
			<BillingDropdownHead v-if="isEditable" />
			<b-thead
				id="tableHead" 	
				class="billing__head sinao-bleu-petrole-bg"
				:style="{ 'background-color': background, color: foreground }"
			>
				<b-tr>
					<b-th
						v-for="(header, index) in enabledHeaders"
						:key="index"
						:width="header.width"
						class="billing__head_cell"
						:style="Object.keys(enabledHeaders).length > 5 && header.translationKey == 'Désignation' ? `width: ${ (35 - ((Object.keys(enabledHeaders).length - 6) * 3))}%` : ''"
					>
						<b-form-input v-model="header.value" :placeholder="header.placeholder" class="billing__input" />
					</b-th>
				</b-tr>
			</b-thead>
			<Draggable
				v-for="(section, sectionIndex) in numbered_sections"
				:key="sectionIndex"
				draggable=".draggable"
				tag="b-tbody"
				handle=".handle"
				:list="section"
			>
				<b-tr
					v-for="(row, rowIndex) in section"
					:key="row.key"
					:class="['section', 'subtotal'].includes(row.component) ? '' : 'draggable'"
					@keydown.tab.exact="tabPress(sectionIndex, rowIndex, $event)"
					@keydown.enter.exact="tabPress(sectionIndex, rowIndex, $event)"
					@keyup.delete="clear(sectionIndex, rowIndex)"
				>
					<!-- displayed if the line is a section header -->
					<BillingSection
						v-if="row.component === 'section'"
						:data-as-props.sync="row.data"
						:section-id="sectionIndex"
						:index="rowIndex"
						:line-number="row.line_number"
						@event-append="appendRow"
						@event-remove="removeRow"
					/>

					<!-- displayed if the line is an entry -->
					<BillingEntry
						v-if="row.component === 'entry'"
						:data-as-props.sync="row.data"
						:section-id="sectionIndex"
						:index="rowIndex"
						:line-number="row.line_number"
						@event-append="appendRow"
						@event-remove="removeRow"
					/>

					<!-- displayed if the line is a comment line -->
					<BillingComment
						v-if="row.component === 'comment'"
						:data-as-props.sync="row.data"
						:section-id="sectionIndex"
						:index="rowIndex"
						:line-number="row.line_number"
						@event-append="appendRow"
						@event-remove="removeRow"
					/>

					<!-- displayed if the line is a subtotal line -->
					<BillingSubtotal
						v-if="row.component === 'subtotal'"
						:section="section"
						:total-excl-tax="
							section.totals && section.totals.totalExclTax ? section.totals.totalExclTax : '0,00'
						"
						:section-id="sectionIndex"
						:line-number="row.line_number"
						@event-append="appendRow"
					/>
				</b-tr>
			</Draggable>
		</b-table-simple>
	</div>
</template>

<style scoped lang="scss">
	@import "../../scss/mixins";

	.billing__table {
		overflow-x: auto;
		white-space: nowrap;
		// table-layout: auto !important;

		::v-deep .billing__cell-designation {
			min-width: 180px !important;
		}

		::v-deep .unit {
			// min-width: 105px !important;
		}

		::v-deep .line_number_cell {
			// min-width: 50px !important;
		}

		::v-deep .small_cell {
			// min-width: 75px !important;
			white-space: normal;
		}
	}

	.sinao-dropdown {
		position: absolute;
		color: rgb(204, 204, 204);
		top: 8px;
		margin-left: -30px;
	}

	.billing__head_cell .billing__input:focus {
		border-color: currentColor !important;
	}

	.billing__head_cell {
		padding: 0;

		&:first-child {
			border-top-left-radius: 6px;
		}

		&:last-child {
			border-top-right-radius: 6px;
		}
	}

	.table tbody + tbody {
		border-top: none;
	}

	.table thead th {
		border-bottom-width: 1px;
	}

	.disable-table a {
		color: var(--white);
	}

	.table thead th {
		border-top: 0px !important;
		border-bottom: 0px !important;
	}
	
</style>
